/**
 * Hook to run async work with loading state. Use instead of repeating
 * setIsLoading(true); try { ... } finally { setIsLoading(false); } in 4+ places.
 */
import { useState, useCallback } from 'react';

/**
 * Returns [loading, runAsync]. runAsync(fn) runs fn(), sets loading true during execution,
 * then false in finally. Caller can use try/catch inside fn for error handling.
 */
export function useAsyncWithLoading(): [
  loading: boolean,
  runAsync: <T>(fn: () => Promise<T>) => Promise<T>,
] {
  const [loading, setLoading] = useState(false);

  const runAsync = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      return await fn();
    } finally {
      setLoading(false);
    }
  }, []);

  return [loading, runAsync];
}
